import type { RouteLocationNormalized } from 'vue-router'
import type { Profile } from '@/modules/auth/domain/entity/auth'
import { firstAllowedRoute } from '@/modules/shared/navigation'

export interface AuthSession {
  isAuthenticated: boolean
  profileLoaded: boolean
  isPlatformAdmin: boolean
  mustChangePassword: boolean
  setProfile(profile: Profile): void
  clearSession(): void
  hasAllPermissions(required: string[]): boolean
}

/** Where a must-change account is always sent — Settings hosts the form
 * already, and it carries no permission gate. */
const PASSWORD_CHANGE_ROUTE = 'settings'

type GuardResult = true | { name: string }

/** Where the platform operator lands, the way `dashboard` is a tenant's. */
const PLATFORM_HOME = 'platform'

/** ...and the door they arrive through, the way `login` is a tenant's. */
const PLATFORM_LOGIN = 'platform-login'

const tenantFallback = (session: AuthSession, to: RouteLocationNormalized): GuardResult => {
  const fallback = firstAllowedRoute((p) => session.hasAllPermissions(p))
  return fallback !== null && fallback !== to.name ? { name: fallback } : { name: 'forbidden' }
}

/**
 * Permission-aware navigation guard. Fail closed on every edge: no
 * session → login, unreadable profile → login, missing permission →
 * first allowed route (or forbidden when nothing is allowed).
 */
export const createNavigationGuard = (deps: {
  getSession: () => AuthSession
  fetchProfile: () => Promise<Profile | null>
}) => {
  return async (to: RouteLocationNormalized): Promise<GuardResult> => {
    const session = deps.getSession()

    if (to.meta.public) {
      if ((to.name === 'login' || to.name === PLATFORM_LOGIN) && session.isAuthenticated) {
        return { name: session.isPlatformAdmin ? PLATFORM_HOME : 'dashboard' }
      }
      return true
    }

    if (!session.isAuthenticated) {
      // Sent to the door they were heading for. Landing on the lender's
      // login after typing /platform is the confusion this whole split
      // exists to remove.
      return { name: to.meta.platform === true ? PLATFORM_LOGIN : 'login' }
    }

    if (!session.profileLoaded) {
      const profile = await deps.fetchProfile()
      if (!profile) {
        // No readable profile means no active membership — drop the
        // session instead of guessing what the user may do.
        session.clearSession()
        return { name: 'login' }
      }
      session.setProfile(profile)
    }

    // Any destination becomes the password screen while the debt stands —
    // the API already refuses everything else, this only keeps the person
    // from being walked into that wall one refusal at a time. A platform
    // operator never carries this flag; only a console-provisioned owner
    // does, and the tenant Settings route is theirs to reach.
    if (session.mustChangePassword && !session.isPlatformAdmin && to.name !== PASSWORD_CHANGE_ROUTE) {
      return { name: PASSWORD_CHANGE_ROUTE }
    }

    // The platform section is gated on identity, not on permissions: the
    // operator holds none, and no tenant permission may ever open it. Two
    // separate doors, and a tenant user gets sent back through theirs.
    if (to.meta.platform === true) {
      return session.isPlatformAdmin ? true : tenantFallback(session, to)
    }

    const required = (to.meta.permissions as string[] | undefined) ?? []
    if (required.length > 0 && !session.hasAllPermissions(required)) {
      // The operator fails every tenant permission by construction, so the
      // permission fallbacks below would walk him through routes he can
      // never open and land him on Forbidden. Send him home instead.
      if (session.isPlatformAdmin) {
        return { name: PLATFORM_HOME }
      }

      // A module the menu offers may be reachable by two roles through two
      // routes. Its own fallback goes first, so someone who may file a
      // request but not decide one lands on their requests rather than on
      // whatever the landing priority happens to allow.
      const declared = to.meta.fallbackRouteName as string | undefined
      if (declared !== undefined && declared !== to.name) {
        return { name: declared }
      }

      return tenantFallback(session, to)
    }

    // A permission-less tenant route (Settings, Forbidden) still belongs to a
    // business. The operator has none, so it is not his to open.
    if (session.isPlatformAdmin) {
      return { name: PLATFORM_HOME }
    }

    return true
  }
}
